import { Flex } from '@mantine/core';

import Resolve from '../../clientstate/components/Resolve';
import RequestTakedown from './RequestTakedown';
import Acknowledge from '../../clientstate/components/Acknowledge';
import DownloadTrigger from '../../downloadreport/DownloadReportTrigger';
import IncidentReport from '../pages/IncidentReport';

import { useIssueState } from '../hooks/useIssueState';
import { incidentsKeys, INCIDENTS_ENDPOINTS } from '../services/keys';
import { unifiedApiKeys } from '@/services/keys';
import { notificationsKeys } from '../../notifications/services/keys';

import { IncidentTicket, OIncidentsStatus } from '../types';
import type { Schema } from '@/types/mercury-data-types/unifiedapi';
import { MercuryTrackersIds } from '@/constants/mercuryPages';
import RequestAddToWatchlist from '@/client/features/incidentmanagement/components/RequestAddToWatchlist';
import { useMercuryPermissions } from '@/hooks/useMercuryPermissions';
import { MODULES } from '@/constants/permissions';
import { POSTHOG_EVENTS, type PosthogEventPayloads } from '@/lib/posthog/events';
import { trackEvent } from '@/lib/posthog/helpers';
import type { IncidentActionButtonVariant } from './incidentActionButton';

interface IncidentActionsProps {
  issueId: number;
  clientIds: Array<string>;
  asButton?: boolean;
  buttonVariant?: IncidentActionButtonVariant;
  loading?: boolean;
  disabled?: boolean;
  print?: boolean;
  clientState?: Schema<'IssueClientResponse'>;
  hasTakedownRequests?: boolean;
  raw?: IncidentTicket;
  category?: string;
  subcategory?: string;
}

enum WatchlistEnabledCategory {
  VIP_DIGITAL_PROTECTION = 'VIP Digital Protection',
}

enum WatchlistEnabledSubcategory {
  SOCIAL_MEDIA_IMPERSONATION = 'Social Media Impersonation',
}

const IncidentActions = ({
  asButton,
  buttonVariant = 'default',
  issueId,
  loading,
  disabled,
  print,
  clientState,
  clientIds,
  hasTakedownRequests,
  raw,
  category,
  subcategory,
}: IncidentActionsProps) => {
  const [status, previousStatus] = useIssueState(clientState, hasTakedownRequests);
  const clientStateBody = [[issueId, clientIds]] as Array<[number, Array<string>]>;
  const hasWatchlistAccess = useMercuryPermissions().hasAccess({
    modules: MODULES.watchlist.modules,
  });
  const isWatchlistEnabledForIncident =
    category === WatchlistEnabledCategory.VIP_DIGITAL_PROTECTION &&
    subcategory === WatchlistEnabledSubcategory.SOCIAL_MEDIA_IMPERSONATION;

  const trackAction = (
    action: PosthogEventPayloads[typeof POSTHOG_EVENTS.INCIDENTS_ACTION_EXECUTE]['action']
  ) => {
    trackEvent(POSTHOG_EVENTS.INCIDENTS_ACTION_EXECUTE, { action, incidentId: issueId });
  };

  return (
    <Flex
      align="center"
      justify={buttonVariant === 'compactOutline' ? 'flex-end' : 'center'}
      gap={buttonVariant === 'compactOutline' ? 8 : 10}
      flex={buttonVariant === 'compactOutline' ? undefined : '1'}
      direction={{
        base: asButton ? 'column' : 'row',
        s: 'row',
      }}
    >
      {print && (
        <DownloadTrigger
          name={`Incident Ticket - ${issueId}`}
          document={<IncidentReport data={raw} />}
          fullButton
          buttonVariant={buttonVariant}
        />
      )}

      {hasWatchlistAccess && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
        <div
          style={{ display: 'contents' }}
          onClickCapture={() => {
            if (!disabled && isWatchlistEnabledForIncident) trackAction('add_to_watchlist');
          }}
        >
          <RequestAddToWatchlist
            issueId={issueId}
            disabled={disabled || !isWatchlistEnabledForIncident}
            loading={loading}
            asButton={asButton}
            buttonVariant={buttonVariant}
          />
        </div>
      )}

      {status?.toLowerCase() !== OIncidentsStatus.Pending &&
        status?.toLowerCase() !== OIncidentsStatus.Resolved && (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
          <div
            style={{ display: 'contents' }}
            onClickCapture={() => {
              if (!disabled && !loading) trackAction('acknowledge');
            }}
          >
            <Acknowledge
              id={clientStateBody}
              isAcknowledged={status === OIncidentsStatus.Acknowledged}
              resource={INCIDENTS_ENDPOINTS.incidents}
              invalidate={[
                notificationsKeys.notificationNew(MercuryTrackersIds.alerts),
                unifiedApiKeys.ticket(INCIDENTS_ENDPOINTS.incidents, issueId),
                incidentsKeys.incidentsGfx(),
                incidentsKeys.incidentsTable(),
                incidentsKeys.incidentsDashboard(),
              ]}
              isLoading={loading}
              disabled={disabled}
              asButton={asButton}
              buttonVariant={buttonVariant}
            />
          </div>
        )}

      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        style={{ display: 'contents' }}
        onClickCapture={() => {
          if (!disabled) trackAction('resolve');
        }}
      >
        <Resolve
          id={clientStateBody}
          status={status}
          resource={INCIDENTS_ENDPOINTS.incidents}
          previousStatus={previousStatus}
          invalidate={[
            unifiedApiKeys.ticket(INCIDENTS_ENDPOINTS.incidents, issueId),
            notificationsKeys.notificationNew(MercuryTrackersIds.alerts),
            incidentsKeys.incidentsGfx(),
            incidentsKeys.incidentsTable(),
            incidentsKeys.incidentsDashboard(),
          ]}
          disabled={disabled}
          asButton={asButton}
          buttonVariant={buttonVariant}
        />
      </div>

      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        style={{ display: 'contents' }}
        onClickCapture={() => {
          if (!disabled && !loading) trackAction('request_takedown');
        }}
      >
        <RequestTakedown
          issueId={issueId}
          disabled={disabled}
          loading={loading}
          asButton={asButton}
          buttonVariant={buttonVariant}
        />
      </div>
    </Flex>
  );
};

export default IncidentActions;
